import React, { useEffect, useRef, useState } from "react";

type RevealProps = React.HTMLAttributes<HTMLElement> & {
  /** Stagger, in ms, applied as a CSS transition-delay. */
  delay?: number;
  /** Render as a different element (default: div). */
  as?: React.ElementType;
  className?: string;
  children: React.ReactNode;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Fades + lifts content into view once, on first intersection.
 *
 * Degrades to "always visible" when the user prefers reduced motion or when
 * IntersectionObserver is unavailable, so content is never trapped behind an
 * animation that will not run.
 */
const Reveal: React.FC<RevealProps> = ({
  delay = 0,
  as: Tag = "div",
  className = "",
  children,
  style,
  ...rest
}) => {
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(
    () => prefersReducedMotion() || typeof IntersectionObserver === "undefined"
  );

  useEffect(() => {
    if (shown) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shown]);

  return (
    <Tag
      ref={ref}
      className={`reveal${shown ? " is-in" : ""}${className ? ` ${className}` : ""}`}
      style={{ ...style, ["--reveal-delay" as string]: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
